import { STEPS, STEPS_MAPPING } from '@constant/steps';
import { useSetCurrentStep } from '@context/MainContext';
import { TransferDetailsContext } from '@context/TransferDetailsContext';
import useScreenViews from '@effects/useScreenViews';
import { handleKeyDown } from '@helpers/OnKeyDown';
import { isEmpty } from '@helpers/String';
import { Search } from '@mui/icons-material';
import { Button, FormHelperText, Grid, ButtonBase, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import TransferTrackingService from '@network/api/transferTracking';
import { useFormik } from 'formik';
import { useContext } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as yup from 'yup';

const useStyles = makeStyles()((theme) => ({
	searchBar: {
		'& input': {
			fontWeight: 'bold',
			color: theme.text.primary,
			'::placeholder': {
				color: theme.text.tertiary
			}
		},
		width: '100%',
		borderRadius: '30px'
	}
}));

export const OrderSearchBar = () => {
	const { setOrderId, setExpires, setDetails, setTimeline, setStatusRefreshedAt }: any = useContext(TransferDetailsContext);
	const { classes } = useStyles();
	const { t } = useTranslation();
	const theme = useTheme();
	const { belowMobileView } = useScreenViews();
	const setCurrentStep = useSetCurrentStep();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const transferTrackingService = new TransferTrackingService();

	const validationSchema = yup.object({
		orderId: yup
			.string()
			.required(t('ENTER_ORDER_NUMBER_PLACEHOLDER'))
			.matches(/^[a-zA-Z0-9]+$/, t('ORDER_NUMBER_VALIDATION'))
	});

	const formik = useFormik({
		initialValues: {
			orderId: ''
		},
		validationSchema,
		validateOnChange: true,
		validateOnBlur: false,
		onSubmit: async (values) => {
			try {
				const { orderId }: any = values || {};
				setOrderId(orderId);
				const newToken = executeRecaptcha && (await executeRecaptcha('CheckOrderDetails'));
				const result = await transferTrackingService.postSearchOrderNumber({ orderId }, newToken);
				const { data: resultData } = result;

				if (!isEmpty(resultData)) {
					setOrderId(resultData.orderId);
					setExpires(resultData.expires);
					setDetails(resultData.details);
					setTimeline(resultData.timeline);
					setStatusRefreshedAt(new Date());

					const state = resultData.timeline[0].activity.state;
					const action = resultData.timeline[0].activity.action;
					const currentStep = STEPS_MAPPING[state][action] ?? STEPS_MAPPING[state];

					setCurrentStep && setCurrentStep(currentStep);
				}
			} catch (error: any) {
				const { status }: any = error?.response?.data || {};

				if (status === 'TrackingExpiredError') {
					setCurrentStep && setCurrentStep(STEPS.LINK_EXPIRED);
				}
				setCurrentStep && setCurrentStep(STEPS.NO_DATA);
				console.error('Error submitting order number', error);
			}
		}
	});

	return (
		<>
			<Grid container sx={{ padding: { xs: '16px 0px', sm: '32px 0px' } }}>
				<Grid item xs={12}>
					<Grid container justifyContent="space-between" alignItems="center">
						<Grid item xs={10} sm={9} md={10} sx={{ pr: { xs: 0, sm: 2 } }}>
							<TextField
								error={formik.touched.orderId && Boolean(formik.errors.orderId)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Search
												sx={{
													color: formik.touched.orderId && Boolean(formik.errors.orderId) ? theme.palette.error.main : theme.palette.text.primary
												}}
											/>
										</InputAdornment>
									),
									sx: {
										'& fieldset': { borderRadius: '30px' },
										'& input': {
											color: formik.touched.orderId && Boolean(formik.errors.orderId) ? theme.palette.error.main : theme.text.primary,
											'::placeholder': {
												color: formik.touched.orderId && Boolean(formik.errors.orderId) ? theme.palette.error.main : theme.text.tertiary
											}
										}
									}
								}}
								value={formik.values.orderId}
								onChange={(e) => {
									formik.setFieldValue('orderId', e.target.value);
								}}
								onKeyDown={(e) => {
									handleKeyDown(e, 'Enter', formik.handleSubmit);
								}}
								placeholder={t('ENTER_ORDER_NUMBER')}
								className={classes.searchBar}
								onBlur={formik.handleBlur}
							/>
						</Grid>
						<Grid item xs={2} sm={3} md={2} sx={{ textAlign: 'end' }}>
							{belowMobileView ? (
								<ButtonBase
									sx={{ padding: '14px', borderRadius: '365px', backgroundColor: theme.palette.primary.main }}
									onClick={() => formik.handleSubmit()}>
									<Search />
								</ButtonBase>
							) : (
								<Button
									variant="contained"
									color="primary"
									sx={{ borderRadius: { md: '30px' }, width: '100%', height: '100%', gap: 1 }}
									onClick={() => formik.handleSubmit()}>
									<Search />
									<Typography variant="button" component="h3">
										{t('SEARCH')}
									</Typography>
								</Button>
							)}
						</Grid>
					</Grid>
				</Grid>
				{formik.touched.orderId && Boolean(formik.errors.orderId) && (
					<Grid item>
						<FormHelperText error>
							<Typography variant="subtitle1" component="span" sx={{ pl: '15px', pt: '5px' }}>
								{formik.errors.orderId}
							</Typography>
						</FormHelperText>
					</Grid>
				)}
			</Grid>
		</>
	);
};
